import { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { Provider as JotaiProvider } from "jotai";
import { QueryClientProvider, QueryClient, useQueryClient } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { ToastProvider } from "@evomatic-ui-kit/toast";
import { Provider as MapProvider } from "@evomatic-ui-kit/map";
import { ThemeProvider, useIsMobile } from "@evomatic-ui-kit/hooks";

import { routeTree } from "./routeTree.gen";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { authStore } from "@/features/auth/store/authStore";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import "@/integrations/i18n/index.ts";
import "./styles.css";
import { setWorkerUrl } from "maplibre-gl";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

setWorkerUrl(workerUrl);

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  // basepath: "/base-url",
  context: {
    auth: authStore,
    isMobile: undefined!,
  },
});

const MINUTE = 1000 * 60;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // disabilita refetch al focus
      refetchOnReconnect: false, // disabilita refetch alla riconnessione
      gcTime: MINUTE * 10, // 10 minuti: quanto resta in memoria se inutilizzata
      staleTime: MINUTE * 5, // 5 minuti: evita refetch se già in cache
      refetchOnMount: false, // opzionale: evita refetch quando il componente si rimonta
      retry: false, // evita richiami aggiuntivi all'api dopo che è fallita
    },
  },
});

export function InnerApp() {
  const isMobile = useIsMobile();
  return <RouterProvider router={router} context={{ auth: authStore, isMobile }} />;
}

function AuthSessionCoordinator() {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const previousStatus = useRef(status);

  useEffect(() => {
    const wasAuthenticated = previousStatus.current === "authenticated";
    previousStatus.current = status;

    if (!wasAuthenticated || status !== "unauthenticated") return;

    void (async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
      await router.invalidate();
    })();
  }, [queryClient, status]);

  return null;
}

createRoot(document.getElementById("root")!).render(
  <JotaiProvider>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthSessionCoordinator />
        <MapProvider>
          <AppErrorBoundary>
            <InnerApp />
          </AppErrorBoundary>
        </MapProvider>
        <ToastProvider position="top-right" />
      </QueryClientProvider>
    </ThemeProvider>
  </JotaiProvider>,
);
